import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Cpu, Smartphone, Monitor, Settings, CheckCircle, Layers, Package, Rocket, Target } from 'lucide-react';

// Hero background images - rotating carousel (premium, classy images)
const HERO_BACKGROUNDS = [
  {
    url: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=80',
    alt: 'Paris - Tour Eiffel'
  },
  {
    url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80',
    alt: 'Innovation space - Modern workspace'
  },
  {
    url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80',
    alt: 'Team working - Office design meeting'
  },
  {
    url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80',
    alt: 'Corporate building skyline'
  },
  {
    url: 'https://images.unsplash.com/photo-1660851983945-c79a9738e557?w=1920&q=80',
    alt: 'Marseille - Vue panoramique moderne'
  }
];

const ABOUT_IMG_1 = 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80';
const ABOUT_IMG_2 = 'https://images.unsplash.com/photo-1556742111-a301076d9d18?w=600&q=80';
const SERVICE_IMG_1 = 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&q=80';
const SERVICE_IMG_2 = 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=80';
const SERVICE_IMG_3 = 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&q=80';
const SERVICE_IMG_4 = 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&q=80';

// Marquee component with better contrast
const Marquee = ({ text, reverse = false }) => (
  <div className="overflow-hidden py-5 border-y border-gray-300 bg-[#EFEBE5]">
    <div className="flex" style={{ animation: `marquee 25s linear infinite ${reverse ? 'reverse' : ''}` }}>
      {[...Array(10)].map((_, i) => (
        <span key={i} className="marquee-item">{text}</span>
      ))}
    </div>
  </div>
);

// Feature card icons (SVG)
const FeatureIcon1 = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 32 32" fill="currentColor">
    <path d="M4.265 7.999c0-2.057 1.674-3.734 3.734-3.734s3.734 1.674 3.734 3.734c0 2.057-1.674 3.734-3.734 3.734s-3.734-1.674-3.734-3.734zM0 7.999c0 4.418 3.581 7.999 7.999 7.999s7.999-3.581 7.999-7.999c0-4.418-3.581-7.999-7.999-7.999s-7.999 3.581-7.999 7.999z"/>
    <path d="M0 24.001c0-4.418 3.581-7.999 7.999-7.999s7.999 3.581 7.999 7.999c0 4.418-3.581 7.999-7.999 7.999s-7.999-3.583-7.999-7.999z"/>
    <path d="M15.999 7.999c0-4.418 3.583-7.999 8.002-7.999s7.999 3.581 7.999 7.999c0 4.418-3.581 7.999-7.999 7.999s-8.002-3.581-8.002-7.999z"/>
    <path d="M20.266 24.001c0-2.057 1.674-3.734 3.734-3.734s3.734 1.674 3.734 3.734c0 2.060-1.674 3.734-3.734 3.734s-3.734-1.677-3.734-3.734zM15.999 24.001c0 4.418 3.581 7.999 7.999 7.999s7.999-3.581 7.999-7.999c0-4.418-3.581-7.999-7.999-7.999s-7.999 3.581-7.999 7.999z"/>
  </svg>
);

const FeatureIcon2 = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 32 32" fill="currentColor">
    <path d="M24.001 5.333c-1.471 0-2.666 1.195-2.666 2.666s1.195 2.667 2.666 2.667 2.666-1.195 2.666-2.667h5.333c0 4.418-3.581 7.999-7.999 7.999s-7.999-3.581-7.999-7.999c-0.002-4.418 3.581-7.999 7.999-7.999v5.333z"/>
    <path d="M5.333 7.999c0 1.471 1.195 2.667 2.666 2.667s2.667-1.195 2.667-2.667c0-1.471-1.195-2.666-2.667-2.666v-5.333c4.418 0 7.999 3.581 7.999 7.999s-3.581 7.999-7.999 7.999c-4.418 0-7.999-3.581-7.999-7.999h5.333z"/>
    <path d="M26.667 24.001c0-1.471-1.195-2.667-2.666-2.667s-2.667 1.195-2.667 2.667c0 1.471 1.195 2.666 2.667 2.666v5.333c-4.418 0-7.999-3.581-7.999-7.999s3.581-7.999 7.999-7.999c4.418 0 7.999 3.581 7.999 7.999h-5.333z"/>
    <path d="M7.999 26.667c1.471 0 2.667-1.195 2.667-2.666s-1.195-2.667-2.667-2.667c-1.471 0-2.666 1.195-2.666 2.667h-5.333c0-4.418 3.581-7.999 7.999-7.999s7.999 3.581 7.999 7.999c0 4.418-3.581 7.999-7.999 7.999v-5.333z"/>
  </svg>
);

const FeatureIcon3 = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 32 32" fill="currentColor">
    <path d="M15.999 24.532c4.117 0 7.466 3.348 7.466 7.466h8.533c0-8.837-7.162-15.999-15.999-15.999s-15.999 7.165-15.999 16.001h8.533c0-4.117 3.348-7.468 7.466-7.468z"/>
    <path d="M15.999 7.466c-4.117 0-7.466-3.348-7.466-7.466h-8.533c0 8.837 7.162 15.999 15.999 15.999s16.001-7.162 16.001-15.999h-8.533c0 4.117-3.351 7.466-7.468 7.466z"/>
  </svg>
);

export function HomePage() {
  const { i18n } = useTranslation();
  const [currentBgIndex, setCurrentBgIndex] = useState(0);

  // Auto-rotate hero background every 20 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % HERO_BACKGROUNDS.length);
    }, 20000);
    return () => clearInterval(interval);
  }, []);
  
  const getText = (fr, ar, en) => {
    if (i18n.language === 'fr') return fr;
    if (i18n.language === 'ar') return ar;
    return en;
  };

  // Hero features - D.One positioning
  const heroFeatures = [
    {
      icon: FeatureIcon1,
      title: getText('Design & Engineering', 'التصميم والهندسة', 'Design & Engineering'),
      desc: getText(
        'Pilotage produit structuré en France : cadrage, spécifications, prototypage et coordination industrielle.',
        'إدارة المنتجات المنظمة في فرنسا: التأطير والمواصفات والنماذج الأولية والتنسيق الصناعي.',
        'Structured product management in France: scoping, specifications, prototyping and industrial coordination.'
      )
    },
    {
      icon: FeatureIcon2,
      title: getText('Sourcing & Industrialisation', 'التوريد والتصنيع', 'Sourcing & Industrialization'),
      desc: getText(
        'Capacité d\'exécution internationale : sourcing optimisé, production maîtrisée et contrôle qualité rigoureux.',
        'قدرة تنفيذ دولية: توريد محسن وإنتاج متقن ومراقبة جودة صارمة.',
        'International execution capability: optimized sourcing, controlled production and rigorous quality control.'
      )
    },
    {
      icon: FeatureIcon3,
      title: getText('Déploiement & Opérations', 'النشر والعمليات', 'Deployment & Operations'),
      desc: getText(
        'De la conception au terrain : logistique, distribution et accompagnement opérationnel multi-marchés.',
        'من التصميم إلى الميدان: اللوجستيات والتوزيع والدعم التشغيلي متعدد الأسواق.',
        'From design to field: logistics, distribution and multi-market operational support.'
      )
    }
  ];

  // 4 pillars of activity
  const pillars = [
    { 
      title: getText('Accessoires mobiles', 'إكسسوارات الهواتف', 'Mobile accessories'),
      desc: getText(
        'Une offre d\'accessoires pensée pour les usages du quotidien et les besoins des réseaux de distribution : charge, audio, connectique, mobilité et équipements embarqués.',
        'مجموعة إكسسوارات مصممة للاستخدام اليومي واحتياجات شبكات التوزيع: الشحن والصوت والتوصيل والتنقل والمعدات المدمجة.',
        'An accessories offering designed for everyday use and distribution network needs: charging, audio, connectivity, mobility and embedded equipment.'
      ),
      image: SERVICE_IMG_1, 
      link: '/accessoires',
      icon: Smartphone
    },
    { 
      title: getText('Produits télécom sur mesure', 'منتجات اتصالات مخصصة', 'Custom telecom products'),
      desc: getText(
        'Conception et pilotage de terminaux et équipements adaptés à des besoins spécifiques : feature phones, modems, connectivité et smartphones ciblés.',
        'تصميم وإدارة الأجهزة والمعدات المكيفة لاحتياجات محددة: الهواتف الأساسية والمودم والاتصال والهواتف الذكية المستهدفة.',
        'Design and management of terminals and equipment adapted to specific needs: feature phones, modems, connectivity and targeted smartphones.'
      ),
      image: SERVICE_IMG_2, 
      link: '/telecom',
      icon: Cpu
    },
    { 
      title: getText('Retail Tech & produits démat', 'تقنية التجزئة والمنتجات الرقمية', 'Retail Tech & digital products'),
      desc: getText(
        'Conception de parcours et d\'outils digitaux pour la distribution, l\'activation et la vente : interfaces, orchestration et produits dématérialisés.',
        'تصميم المسارات والأدوات الرقمية للتوزيع والتفعيل والبيع: الواجهات والتنسيق والمنتجات الرقمية.',
        'Design of digital journeys and tools for distribution, activation and sales: interfaces, orchestration and digital products.'
      ),
      image: SERVICE_IMG_3, 
      link: '/bureau-etude',
      icon: Monitor
    },
    { 
      title: getText('Bureau d\'étude & exécution', 'مكتب الدراسات والتنفيذ', 'Engineering office & execution'),
      desc: getText(
        'Du besoin métier au déploiement : spécifications, prototypage, sourcing, industrialisation, QA et coordination opérationnelle.',
        'من الحاجة المهنية إلى النشر: المواصفات والنماذج الأولية والتوريد والتصنيع وضمان الجودة والتنسيق التشغيلي.',
        'From business need to deployment: specifications, prototyping, sourcing, industrialization, QA and operational coordination.'
      ),
      image: SERVICE_IMG_4, 
      link: '/groupe',
      icon: Settings
    }
  ];

  // Methodology steps
  const methodologySteps = [
    { num: '01', title: getText('Cadrage du besoin', 'تأطير الحاجة', 'Need assessment'), icon: Target },
    { num: '02', title: getText('Spécifications & architecture', 'المواصفات والهندسة', 'Specifications & architecture'), icon: Layers },
    { num: '03', title: getText('Prototype & validation', 'النموذج والتحقق', 'Prototype & validation'), icon: Package },
    { num: '04', title: getText('Sourcing & industrialisation', 'التوريد والتصنيع', 'Sourcing & industrialization'), icon: Settings },
    { num: '05', title: getText('Déploiement & suivi', 'النشر والمتابعة', 'Deployment & monitoring'), icon: Rocket }
  ];

  return (
    <div data-testid="home-page" className="bg-[#F8F6F3]">
      
      {/* ============================================
          HERO SECTION - D.One Group positioning
      ============================================ */}
      <section className="relative min-h-screen" data-testid="hero-section">
        {/* Background Image Carousel */}
        <div className="absolute inset-0 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentBgIndex}
              src={HERO_BACKGROUNDS[currentBgIndex].url}
              alt={HERO_BACKGROUNDS[currentBgIndex].alt}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
              className="w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
        </div>
        
        {/* Background indicator dots */}
        <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
          {HERO_BACKGROUNDS.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentBgIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentBgIndex 
                  ? 'w-8 bg-[#f4b00f]' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container-main pt-32 flex flex-col min-h-screen">
          {/* Main headline - centered */}
          <div className="flex-1 flex flex-col items-center justify-center text-center py-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="text-marquee-label text-white/70">
                {getText('D.ONE GROUP', 'مجموعة دي.وان', 'D.ONE GROUP')}
              </span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-4xl md:text-6xl lg:text-7xl font-semibold text-white mb-6 max-w-5xl leading-tight"
              data-testid="hero-headline"
            >
              {getText(
                'Conception produit télécom & retail tech, pilotée en France',
                'تصميم منتجات الاتصالات وتقنية التجزئة، بقيادة فرنسية',
                'Telecom & retail tech product design, led from France'
              )}
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg md:text-xl text-white/70 mb-10 max-w-3xl leading-relaxed"
            >
              {getText(
                'Nous concevons et orchestrons des solutions hardware et software pour des usages terrain réels : accessoires, terminaux, connectivité et produits digitaux de distribution.',
                'نصمم وننسق حلول الأجهزة والبرمجيات للاستخدامات الميدانية الحقيقية: الإكسسوارات والأجهزة والاتصال والمنتجات الرقمية للتوزيع.',
                'We design and orchestrate hardware and software solutions for real-world field use: accessories, terminals, connectivity and digital distribution products.'
              )}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link to="/groupe" className="btn-primary" data-testid="hero-cta-1">
                {getText('Découvrir le groupe', 'اكتشف المجموعة', 'Discover the group')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-outline-light" data-testid="hero-cta-2">
                {getText('Parler d\'un projet', 'تحدث عن مشروع', 'Discuss a project')}
              </Link>
            </motion.div>
          </div>

          {/* Bottom section - blur cards */}
          <div className="pb-16">
            <div className="grid md:grid-cols-3 gap-4">
              {heroFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                  className="relative p-8 rounded-[24px] overflow-hidden group"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.15)'
                  }}
                >
                  <div className="text-[#f4b00f] mb-6">
                    <feature.icon />
                  </div>
                  <div className="w-full h-px bg-white/20 mb-6" />
                  <h3 className="text-lg font-semibold text-white mb-3">{feature.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          MARQUEE - "qui sommes-nous"
      ============================================ */}
      <Marquee text={getText('qui sommes-nous', 'من نحن', 'who we are')} />

      {/* ============================================
          ABOUT SECTION - Intro D.One
      ============================================ */}
      <section className="section-xl" data-testid="about-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Left - Images */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="rounded-[30px] overflow-hidden">
                <img src={ABOUT_IMG_1} alt="Engineering" className="w-full aspect-[4/3] object-cover" />
              </div>
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="absolute -bottom-10 -right-6 lg:-right-12 w-48 lg:w-64 rounded-[24px] overflow-hidden shadow-2xl border-4 border-white"
              >
                <img src={ABOUT_IMG_2} alt="Product Design" className="w-full aspect-square object-cover" />
              </motion.div>
              {/* French Tech logo badge */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="absolute top-8 -left-4 lg:-left-8 bg-white p-4 rounded-[16px] shadow-xl"
              >
                <img src="/images/french-tech-logo.png" alt="French Tech" className="h-12 w-auto" />
              </motion.div>
            </motion.div>
            
            {/* Right - Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-marquee-label mb-6 block">
                {getText('LE GROUPE', 'المجموعة', 'THE GROUP')}
              </span>
              <h2 className="text-display mb-8">
                {getText(
                  'Conception, sourcing et déploiement de solutions télécom & retail tech',
                  'تصميم وتوريد ونشر حلول الاتصالات وتقنية التجزئة',
                  'Design, sourcing and deployment of telecom & retail tech solutions'
                )}
              </h2>
              
              <div className="space-y-6 mb-10">
                <p className="text-body-lg">
                  {getText(
                    'D.One est un groupe de conception, sourcing et déploiement de solutions télécom & retail tech. Nous combinons design produit, engineering, industrialisation et exécution opérationnelle pour livrer des solutions concrètes, adaptées aux contraintes de marché.',
                    'دي.وان هي مجموعة لتصميم وتوريد ونشر حلول الاتصالات وتقنية التجزئة. نجمع بين تصميم المنتجات والهندسة والتصنيع والتنفيذ التشغيلي لتقديم حلول ملموسة تتكيف مع قيود السوق.',
                    'D.One is a design, sourcing and deployment group for telecom & retail tech solutions. We combine product design, engineering, industrialization and operational execution to deliver concrete solutions adapted to market constraints.'
                  )}
                </p>
                <p className="text-body-lg">
                  {getText(
                    'Notre approche repose sur un pilotage produit structuré : cadrage du besoin, spécifications, choix techniques, prototypage, coordination industrielle et préparation du déploiement.',
                    'يعتمد نهجنا على إدارة منتج منظمة: تأطير الحاجة والمواصفات والخيارات التقنية والنماذج الأولية والتنسيق الصناعي وإعداد النشر.',
                    'Our approach is based on structured product management: need assessment, specifications, technical choices, prototyping, industrial coordination and deployment preparation.'
                  )}
                </p>
              </div>

              {/* Key stats */}
              <div className="grid grid-cols-3 gap-6 mb-10 py-8 border-y border-gray-200">
                {[
                  { value: getText('Hardware', 'الأجهزة', 'Hardware'), label: getText('+ Software', '+ البرمجيات', '+ Software') },
                  { value: getText('Design', 'التصميم', 'Design'), label: getText('in France', 'في فرنسا', 'in France') },
                  { value: getText('Multi', 'متعدد', 'Multi'), label: getText('marchés', 'الأسواق', 'markets') }
                ].map((item, i) => (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#f4b00f]">{item.value}</div>
                    <div className="text-sm text-gray-500 mt-1">{item.label}</div>
                  </div>
                ))}
              </div>
              
              <Link to="/groupe" className="btn-dark">
                {getText('Découvrir le groupe', 'اكتشف المجموعة', 'Discover the group')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================================
          DESIGN & ENGINEERING IN FRANCE BLOCK
      ============================================ */}
      <section className="py-16 bg-[#0A0A0A]" data-testid="france-block">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center justify-between gap-8"
          >
            <div className="max-w-2xl">
              <span className="text-marquee-label text-white/50 mb-4 block">
                {getText('CONCEPTION & PILOTAGE PRODUIT EN FRANCE', 'تصميم وإدارة المنتجات في فرنسا', 'PRODUCT DESIGN & MANAGEMENT IN FRANCE')}
              </span>
              <p className="text-lg text-white/70 leading-relaxed">
                {getText(
                  'Nous mettons l\'accent sur la pertinence d\'usage, la qualité d\'exécution et l\'équilibre entre performance, coût et time-to-market. Une culture produit inspirée des standards French Tech : rapide, structurée et orientée exécution.',
                  'نركز على ملاءمة الاستخدام وجودة التنفيذ والتوازن بين الأداء والتكلفة ووقت الوصول إلى السوق. ثقافة منتج مستوحاة من معايير French Tech: سريعة ومنظمة وموجهة نحو التنفيذ.',
                  'We focus on usage relevance, execution quality and the balance between performance, cost and time-to-market. A product culture inspired by French Tech standards: fast, structured and execution-oriented.'
                )}
              </p>
            </div>
            <div className="flex items-center gap-3 px-6 py-4 rounded-full border border-white/20">
              <div className="w-3 h-3 rounded-full bg-[#f4b00f]" />
              <span className="text-white font-medium">
                {getText('French-led product design', 'تصميم منتج بقيادة فرنسية', 'French-led product design')}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================
          MARQUEE - "nos activités"
      ============================================ */}
      <Marquee text={getText('nos activités', 'أنشطتنا', 'our activities')} reverse />

      {/* ============================================
          4 PILLARS SECTION
      ============================================ */}
      <section className="section-xl bg-white" data-testid="pillars-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-marquee-label mb-6 block">
              {getText('4 PILIERS D\'ACTIVITÉ', '4 أعمدة نشاط', '4 ACTIVITY PILLARS')}
            </span>
            <h2 className="text-display max-w-3xl mx-auto">
              {getText(
                'Hardware, software et exécution terrain',
                'الأجهزة والبرمجيات والتنفيذ الميداني',
                'Hardware, software and field execution'
              )}
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={pillar.link}
                  className="group block bg-[#F8F6F3] rounded-[24px] overflow-hidden hover:shadow-xl transition-all duration-500"
                  data-testid={`pillar-card-${index}`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="md:w-2/5 overflow-hidden">
                      <img 
                        src={pillar.image} 
                        alt={pillar.title}
                        className="w-full h-48 md:h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    {/* Content */}
                    <div className="md:w-3/5 p-8">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#ffcc4d] via-[#f4b00f] to-[#d99a00] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <pillar.icon className="w-6 h-6 text-black" />
                      </div>
                      <h3 className="text-xl font-semibold mb-4 group-hover:text-[#f4b00f] transition-colors">
                        {pillar.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed mb-6">
                        {pillar.desc}
                      </p>
                      <div className="flex items-center gap-2 text-sm font-medium text-[#f4b00f]">
                        {getText('En savoir plus', 'اعرف المزيد', 'Learn more')}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          MARQUEE - "méthodologie"
      ============================================ */}
      <Marquee text={getText('de l\'idée au déploiement', 'من الفكرة إلى النشر', 'from idea to deployment')} />

      {/* ============================================
          METHODOLOGY SECTION - More detailed
      ============================================ */}
      <section className="section-xl bg-[#0A0A0A]" data-testid="methodology-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-marquee-label text-white/50 mb-6 block">
              {getText('MÉTHODOLOGIE', 'المنهجية', 'METHODOLOGY')}
            </span>
            <h2 className="text-display text-white max-w-3xl mx-auto">
              {getText(
                'De l\'idée au déploiement',
                'من الفكرة إلى النشر',
                'From idea to deployment'
              )}
            </h2>
            <p className="text-white/50 mt-6 max-w-2xl mx-auto">
              {getText(
                'Une approche structurée pour transformer vos besoins en solutions concrètes.',
                'نهج منظم لتحويل احتياجاتك إلى حلول ملموسة.',
                'A structured approach to transform your needs into concrete solutions.'
              )}
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-5 gap-6">
            {[
              { 
                num: '01', 
                title: getText('Cadrage', 'التأطير', 'Scoping'),
                desc: getText('Analyse du besoin, contexte marché, contraintes techniques et budgétaires.', 'تحليل الاحتياجات، سياق السوق، القيود التقنية والميزانية.', 'Need analysis, market context, technical and budget constraints.'),
                icon: Target
              },
              { 
                num: '02', 
                title: getText('Spécifications', 'المواصفات', 'Specifications'),
                desc: getText('Architecture produit, choix techniques, documentation fonctionnelle.', 'بنية المنتج، الخيارات التقنية، التوثيق الوظيفي.', 'Product architecture, technical choices, functional documentation.'),
                icon: Layers
              },
              { 
                num: '03', 
                title: getText('Prototype', 'النموذج', 'Prototype'),
                desc: getText('Développement, tests fonctionnels, validation avant production.', 'التطوير، الاختبارات الوظيفية، التحقق قبل الإنتاج.', 'Development, functional tests, pre-production validation.'),
                icon: Package
              },
              { 
                num: '04', 
                title: getText('Industrialisation', 'التصنيع', 'Industrialization'),
                desc: getText('Sourcing fournisseurs, production série, contrôle qualité.', 'توريد الموردين، الإنتاج الضخم، مراقبة الجودة.', 'Supplier sourcing, mass production, quality control.'),
                icon: Settings
              },
              { 
                num: '05', 
                title: getText('Déploiement', 'النشر', 'Deployment'),
                desc: getText('Logistique, mise en marché, support opérationnel continu.', 'اللوجستيات، طرح السوق، الدعم التشغيلي المستمر.', 'Logistics, market launch, ongoing operational support.'),
                icon: Rocket
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group"
              >
                <div className="bg-white/5 backdrop-blur-sm rounded-[20px] p-6 h-full border border-white/10 hover:border-[#f4b00f]/50 hover:bg-white/10 transition-all duration-300">
                  {/* Number */}
                  <span className="text-6xl font-bold text-[#f4b00f]/20 group-hover:text-[#f4b00f]/40 transition-colors block mb-4">
                    {step.num}
                  </span>
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#ffcc4d] via-[#f4b00f] to-[#d99a00] flex items-center justify-center mb-4">
                    <step.icon className="w-6 h-6 text-black" />
                  </div>
                  {/* Title */}
                  <h4 className="font-semibold text-white text-lg mb-3">{step.title}</h4>
                  {/* Description */}
                  <p className="text-white/50 text-sm leading-relaxed">{step.desc}</p>
                </div>
                {/* Connector arrow */}
                {index < 4 && (
                  <div className="hidden md:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-6 h-6 text-[#f4b00f]/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          CTA SECTION
      ============================================ */}
      <section className="section-xl" data-testid="cta-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-[40px]"
          >
            <div className="absolute inset-0 bg-[#0A0A0A]" style={{ borderRadius: '40px 40px 40px 100px' }} />
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#f4b00f]/10 rounded-full blur-3xl" />
            
            <div className="relative z-10 p-12 md:p-20 text-center">
              <span className="text-marquee-label text-white/60 mb-6 block">
                {getText('PARLONS DE VOTRE PROJET', 'لنتحدث عن مشروعك', 'LET\'S DISCUSS YOUR PROJECT')}
              </span>
              <h2 className="text-3xl md:text-5xl font-semibold text-white mb-6">
                {getText(
                  'Un projet hardware, software ou retail tech ?',
                  'مشروع أجهزة أو برمجيات أو تقنية تجزئة؟',
                  'A hardware, software or retail tech project?'
                )}
              </h2>
              <p className="text-white/60 mb-10 max-w-xl mx-auto">
                {getText(
                  'De la conception au déploiement, nous accompagnons vos projets télécom et retail tech.',
                  'من التصميم إلى النشر، نرافق مشاريعكم في الاتصالات وتقنية التجزئة.',
                  'From design to deployment, we support your telecom and retail tech projects.'
                )}
              </p>
              <Link to="/contact" className="btn-primary">
                {getText('Parler d\'un projet', 'تحدث عن مشروع', 'Discuss a project')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================
          ECOSYSTEM SECTION
      ============================================ */}
      <section className="section-xl bg-white" data-testid="ecosystem-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-marquee-label mb-6 block">
              {getText('ÉCOSYSTÈME GROUPE', 'النظام البيئي للمجموعة', 'GROUP ECOSYSTEM')}
            </span>
            <h2 className="text-display">
              {getText('Nos entités', 'كياناتنا', 'Our entities')}
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* epaas.io - Violet/Purple theme */}
            <motion.a
              href="https://epaas.io"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group block p-8 rounded-[24px] transition-all duration-500 hover:shadow-2xl"
              style={{ background: 'linear-gradient(135deg, #4C1D95 0%, #7C3AED 100%)' }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-white">epaas.io</h3>
                <div className="w-12 h-12 rounded-full border-2 border-white/30 flex items-center justify-center group-hover:bg-white group-hover:border-white transition-all">
                  <ArrowUpRight className="w-5 h-5 text-white group-hover:text-[#7C3AED] transition-colors" />
                </div>
              </div>
              <p className="text-white/80 mb-4">
                {getText(
                  'Eproduct as a Service',
                  'المنتج الإلكتروني كخدمة',
                  'Eproduct as a Service'
                )}
              </p>
              <div className="flex items-center gap-2 text-sm text-white/60">
                <span className="w-2 h-2 rounded-full bg-green-400" />
                {getText('En ligne', 'متصل', 'Online')}
              </div>
            </motion.a>

            {/* interfracom.com - Dark with Orange accents */}
            <motion.a
              href="https://interfracom.com"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group block p-8 rounded-[24px] transition-all duration-500 hover:shadow-2xl"
              style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-[#F97316]">interfracom.com</h3>
                <div className="w-12 h-12 rounded-full border-2 border-[#F97316]/50 flex items-center justify-center group-hover:bg-[#F97316] group-hover:border-[#F97316] transition-all">
                  <ArrowUpRight className="w-5 h-5 text-[#F97316] group-hover:text-black transition-colors" />
                </div>
              </div>
              <p className="text-white/70 mb-4">
                {getText(
                  'Plateforme de distribution de produits dématérialisés. Recharges mobiles, cartes cadeaux, eSIM, transfert de crédit.',
                  'منصة توزيع المنتجات الرقمية. شحن الهاتف، بطاقات الهدايا، eSIM، تحويل الرصيد.',
                  'Digital products distribution platform. Mobile top-ups, gift cards, eSIM, credit transfer.'
                )}
              </p>
              <div className="flex items-center gap-2 text-sm text-white/50">
                <span className="w-2 h-2 rounded-full bg-[#F97316]" />
                {getText('1000+ points de vente', '1000+ نقطة بيع', '1000+ points of sale')}
              </div>
            </motion.a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
